import React, { useContext, useState } from 'react';
import moment from 'moment';

import { Button } from 'reactstrap';
import { getTimeSpanParsed, TimeDisplayFormat, TimeFormat } from '@/utils/time';
import { StyledTimePicker, TimeSlotRow } from '../TimePickerElements/styled';
import { IconButton } from '../Button/Button';
import { FaRegCalendarPlus, FaTrashAlt, FaArrowRight } from 'react-icons/all';
import i18n from '@/utils/i18n';
import { ThemeContext } from '../../providers/ThemeContextProvider';

function findDay(openingHoursMulti, dayNumber) {
  return (openingHoursMulti || []).find((day) => day.day === dayNumber);
}

function sortOpeningHours(openingHours) {
  return [...openingHours].sort((a, b) => a.day - b.day);
}

export default function TimeSpanPickerMulti({
  openingHoursMulti,
  onChange,
  disabled,
}) {
  const { isDarkMode } = useContext(ThemeContext);

  const dayNumbers = [1, 2, 3, 4, 5, 6, 7];
  const defaultAllDaysSlots = [
    `${moment({ hour: 10, minute: 0 }).format(TimeFormat)} - ${moment({
      hour: 13,
      minute: 0,
    }).format(TimeFormat)}`,
    `${moment({ hour: 15, minute: 0 }).format(TimeFormat)} - ${moment({
      hour: 22,
      minute: 0,
    }).format(TimeFormat)}`,
  ];
  const [defaultSlots, setDefaultSlots] = useState(defaultAllDaysSlots);

  const updateParsedTimeSlot = (
    parsedTimeSlot,
    updatedSlotTime,
    openOrClose,
  ) => {
    let openingMoment = parsedTimeSlot.opens;
    let closingMoment = parsedTimeSlot.close;
    if (openOrClose === 'opens') {
      openingMoment = updatedSlotTime;
    } else if (openOrClose === 'close') {
      closingMoment = updatedSlotTime;
    } else {
      throw new Error(`unknown timeType ${openOrClose}`);
    }

    return { opens: openingMoment, closes: closingMoment };
  };

  const updateDefaultTimeSlots = (slotIndex, updatedSlotTime, openOrClose) => {
    if (updatedSlotTime === null) {
      return;
    }
    const parsedTimeSlot = getTimeSpanParsed(defaultSlots[slotIndex]);
    const { opens, closes } = updateParsedTimeSlot(
      parsedTimeSlot,
      updatedSlotTime,
      openOrClose,
    );
    let newSlots = defaultSlots;
    newSlots[slotIndex] = `${opens.format(TimeFormat)} - ${closes.format(
      TimeFormat,
    )}`;
    setDefaultSlots((prevSlots) => [...newSlots]);
  };

  const removeDefaultTimeSlot = (slotIndex) => {
    const newSlots = defaultSlots;
    newSlots.splice(slotIndex, 1);
    setDefaultSlots((prevSlots) => [...newSlots]);
  };

  const addDefaultTimeSlot = () => {
    const newSlot = `${moment({ hour: 10, minute: 0 }).format(
      TimeFormat,
    )} - ${moment({ hour: 22, minute: 0 }).format(TimeFormat)}`;
    setDefaultSlots((prevSlots) => [...prevSlots, newSlot]);
  };

  function onChangeWithFilter(openingHoursMulti) {
    onChange(
      openingHoursMulti.filter(
        (day) => Array.isArray(day.time) && day.time.length !== 0,
      ),
    );
  }

  function removeSlot(dayNumber, timeIndex) {
    let newOpeningHoursMulti = [...openingHoursMulti];
    const dayToUpdate = findDay(newOpeningHoursMulti, dayNumber);
    // remove slot
    const newTime = [...dayToUpdate.time];
    newTime.splice(timeIndex, 1);
    const newDay = { day: dayToUpdate.day, time: newTime };
    openingHoursMulti = openingHoursMulti.filter((d) => d.day !== dayNumber);
    if (newDay.time) {
      openingHoursMulti.push(newDay);
    }
    openingHoursMulti = sortOpeningHours(openingHoursMulti);
    onChangeWithFilter(openingHoursMulti);
  }

  function updateTime(dayNumber, timeIndex, updatedSlotTime, openOrClose) {
    let newOpeningHoursMulti = [...openingHoursMulti];
    const dayToUpdate = findDay(newOpeningHoursMulti, dayNumber);

    // removing all text from input, gives null, ignore this
    if (updatedSlotTime === null) {
      return;
    }

    const parsedTimeSlot = getTimeSpanParsed(dayToUpdate.time[timeIndex]);
    const { opens, closes } = updateParsedTimeSlot(
      parsedTimeSlot,
      updatedSlotTime,
      openOrClose,
    );
    const newTime = [...dayToUpdate.time];
    newTime[timeIndex] = `${opens.format(TimeFormat)} - ${closes.format(
      TimeFormat,
    )}`;

    const newDay = { day: dayNumber, time: newTime };
    newOpeningHoursMulti = newOpeningHoursMulti.filter(
      (day) => day.day !== dayNumber,
    );
    newOpeningHoursMulti.push(newDay);
    newOpeningHoursMulti = sortOpeningHours(newOpeningHoursMulti);
    onChangeWithFilter(newOpeningHoursMulti);
  }

  function onAddTimeslotClick(dayNumber) {
    let newOpeningHoursMulti = [...openingHoursMulti];
    const dayObj = findDay(newOpeningHoursMulti, dayNumber);
    const newTimeStr = '10:42AM - 11:42AM';
    if (dayObj) {
      const newDay = {
        day: dayObj.day,
        time: dayObj.time.concat([newTimeStr]),
      };
      newOpeningHoursMulti = newOpeningHoursMulti.filter(
        (d) => d.day !== dayNumber,
      );
      newOpeningHoursMulti.push(newDay);
    } else {
      newOpeningHoursMulti.push({ day: dayNumber, time: [newTimeStr] });
    }
    newOpeningHoursMulti = sortOpeningHours(newOpeningHoursMulti);
    onChangeWithFilter(newOpeningHoursMulti);
  }

  function applySetAllClick() {
    let newOpeningHoursMulti = [];
    for (let i = 1; i < 8; i++) {
      newOpeningHoursMulti.push({ day: i, time: [...defaultSlots] });
    }
    onChangeWithFilter(newOpeningHoursMulti);
  }

  return (
    <div className="card-group d-flex flex-nowrap overflow-auto pb-3">
      <div
        key="setAll"
        className="card flex-shrink-0 flex-grow-0"
        style={{ flexBasis: '270px' }}
      >
        <div className="card-header">
          <h5 className={'m-0 text-center'}>
            {i18n.t('OpeningHours:setAll')} <FaArrowRight />
          </h5>
        </div>
        <div className="card-body p-2">
          <TimeSlotRow className="flex-grow-1">
            {defaultSlots.map((slot, i) => {
              const slotParsed = getTimeSpanParsed(slot);

              return (
                <TimeSlotRow key={i} isErr={false} className="flex-grow-1 p-3">
                  <div className="d-flex align-items-center flex-grow-1">
                    <div className="d-flex flex-row">
                      <div className="me-2">
                        <label
                          className={`me-1 mb-0 ${
                            isDarkMode ? 'text-white' : 'text-black'
                          }`}
                        >
                          {i18n.t('OpeningHours:opens')}:
                        </label>
                        <StyledTimePicker
                          showSecond={false}
                          defaultValue={slotParsed.opens}
                          value={slotParsed.opens}
                          onChange={(newValue) =>
                            updateDefaultTimeSlots(i, newValue, 'opens')
                          }
                          format={TimeDisplayFormat}
                          focusOnOpen={true}
                          use12Hours={false}
                          allowEmpty={false}
                          minuteStep={1}
                          disabled={disabled}
                        />
                      </div>
                      <div className="me-2">
                        <label
                          className={`me-1 mb-0 ${
                            isDarkMode ? 'text-white' : 'text-black'
                          }`}
                        >
                          {i18n.t('OpeningHours:closes')}:
                        </label>
                        <StyledTimePicker
                          showSecond={false}
                          defaultValue={slotParsed.close}
                          value={slotParsed.close}
                          focusOnOpen={true}
                          onChange={(newValue) =>
                            updateDefaultTimeSlots(i, newValue, 'close')
                          }
                          format={TimeDisplayFormat}
                          use12Hours={false}
                          allowEmpty={false}
                          minuteStep={1}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                    <div className="align-self-end">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeDefaultTimeSlot(i)}
                        disabled={disabled}
                      >
                        <FaTrashAlt />
                      </Button>
                    </div>
                  </div>
                </TimeSlotRow>
              );
            })}
          </TimeSlotRow>
        </div>
        <div className="card-footer">
          <IconButton
            className="mb-2 w-100"
            icon={<FaRegCalendarPlus />}
            label={i18n.t('OpeningHours:addTimeSlot')}
            onClick={addDefaultTimeSlot}
            disabled={disabled}
          />
          <IconButton
            className="w-100"
            label={i18n.t('OpeningHours:setAll').toUpperCase()}
            onClick={applySetAllClick}
            disabled={disabled}
          />
        </div>
      </div>
      {dayNumbers.map((dayNumber) => {
        const dayRaw = findDay(openingHoursMulti, dayNumber);

        return (
          <div
            key={dayNumber}
            className="card flex-shrink-0 flex-grow-0"
            style={{ flexBasis: '270px' }}
          >
            <div className="card-header">
              <h5 className="m-0 text-center">
                {i18n.t(`Common:Day:${dayNumber}`)}
              </h5>
            </div>
            <div className="card-body p-2">
              {!dayRaw && (
                <p className="p-2 text-center">
                  {i18n.t('OpeningHours:closed')}
                </p>
              )}
              {dayRaw &&
                dayRaw.time.map((timeStr, i) => {
                  const day = getTimeSpanParsed(timeStr);
                  return (
                    <TimeSlotRow
                      key={i}
                      isErr={false}
                      className="flex-grow-1 p-3"
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        <div className="d-flex flex-row">
                          <div className="me-2">
                            <label className="me-1 mb-0">
                              {i18n.t('OpeningHours:opens')}:
                            </label>
                            <StyledTimePicker
                              showSecond={false}
                              defaultValue={day ? day.opens : null}
                              value={day ? day.opens : null}
                              onChange={(newValue) =>
                                updateTime(dayNumber, i, newValue, 'opens')
                              }
                              format={TimeDisplayFormat}
                              focusOnOpen={true}
                              use12Hours={false}
                              allowEmpty={false}
                              minuteStep={1}
                              disabled={disabled}
                            />
                          </div>
                          <div className="me-2">
                            <label className="me-1 mb-0">
                              {i18n.t('OpeningHours:closes')}:
                            </label>
                            <StyledTimePicker
                              showSecond={false}
                              defaultValue={day ? day.close : null}
                              value={day ? day.close : null}
                              focusOnOpen={true}
                              onChange={(newValue) =>
                                updateTime(dayNumber, i, newValue, 'close')
                              }
                              format={TimeDisplayFormat}
                              use12Hours={false}
                              allowEmpty={false}
                              minuteStep={1}
                              disabled={disabled}
                            />
                          </div>
                        </div>
                        <div className="align-self-end">
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => removeSlot(dayNumber, i)}
                            disabled={disabled}
                          >
                            <FaTrashAlt />
                          </Button>
                        </div>
                      </div>
                    </TimeSlotRow>
                  );
                })}
            </div>
            <div className="card-footer">
              <IconButton
                className="w-100"
                icon={<FaRegCalendarPlus />}
                label={i18n.t('OpeningHours:addTimeSlot')}
                onClick={() => onAddTimeslotClick(dayNumber)}
                disabled={disabled}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
